import { Box } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFns";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useParams } from "react-router";
import { updateClientCode } from "@entrilia/shared/api/clientApiContext";
import { authState } from "@entrilia/shared/auth/b2c/redirects";
import { useAuth } from "@entrilia/shared/auth/useAuth";
import AuthenticationFailed from "@entrilia/shared/components/AuthenticationFailed";
import ErrorBoundary from "@entrilia/shared/components/ErrorBoundary";
import GlobalNotificationErrorSnackbar from "@entrilia/shared/components/GlobalNotificationErrorSnackbar";
import GlobalNotificationSnackbar from "@entrilia/shared/components/GlobalNotificationSnackbar";
import OutdatedBrowserAlert from "@entrilia/shared/components/OutdatedBrowserAlert";
import FullScreenLoader from "@entrilia/shared/components/fullScreenLoader/FullScreenLoader";
import { NotificationContextProvider } from "@entrilia/shared/contexts/NotificationContextProvider";
import { getUserLocaleInfo } from "@entrilia/shared/utilities/userLocaleConfig";
import Pages from "./Pages";
import AccessCheckPage from "./auth/AccessCheckPage";
import ClientSelectionPage from "./components/clientSelection/ClientSelectionPage";
import ConsentAgreementContainer from "./components/consentAgreement/ConsentAgreementContainer";
import Header from "./components/header/Header";
import ImpersonationPanel from "./components/impersonation/ImpersonationPanel";
import { ClientContextProvider } from "./contexts/ClientContext";
import { ImpersonationBlockContextProvider } from "./contexts/ImpersonationBlockContext";
import { redirectToB2CAuthWithImpersonation } from "./impersonation/impersonationAuth";
import { impersonationStateParams } from "./impersonation/impersonationHandler";
import CustomThemeProvider from "./providers/CustomThemeProvider";
import { getUnsignedGeneralAgreements } from "./services/consentAgreementService";
import { UserActions } from "./store/state/user/actions";
import {
  identityLoadingStateSelector,
  impersonationSelector,
  permissionsSelector,
  sessionClientCodeSelector,
  userConsentAgreementsSelector,
} from "./store/state/user/selectors";
import { loadUserInfo, reportClientSwitch } from "./store/state/user/thunks";
import { IdentityLoadingState } from "./store/state/user/types";
import type { AppDispatch } from "./store/store";

// Do not change - B2C login pages rely on it for branding
export const placeholderClientParam = "entrilia";

const getImpersonationFundName = (isImpersonationMode: boolean) => {
  if (!isImpersonationMode) {
    return "";
  }

  const urlParams = new URLSearchParams(window.location.search);
  return decodeURIComponent(urlParams.get(impersonationStateParams.fund) || "");
};

const App = () => {
  const dispatch: AppDispatch = useDispatch();

  const { client } = useParams();
  const clientParam = client ? client.toLowerCase() : undefined;

  const identityLoadingState = useSelector(identityLoadingStateSelector);
  const isImpersonationMode = useSelector(impersonationSelector);
  const permissions = useSelector(permissionsSelector);
  const userConsentAgreements = useSelector(userConsentAgreementsSelector);
  const sessionClientCode = useSelector(sessionClientCodeSelector);
  const loadedClientParamRef = useRef<string | undefined>(undefined);

  const setSessionClient = useCallback(() => {
    if (clientParam && clientParam !== placeholderClientParam) {
      updateClientCode(clientParam);
    }

    loadedClientParamRef.current = clientParam;
  }, [clientParam]);

  useEffect(() => {
    authState["portalName"] = "Investor Portal";
    authState["client"] = clientParam;

    if (clientParam) {
      dispatch(UserActions.UpdateClientCode(clientParam));
    }
  }, [clientParam, dispatch]);

  const onAuthenticated = useCallback(() => {
    dispatch(UserActions.Authenticated());
    setSessionClient();
    dispatch(loadUserInfo(clientParam));
  }, [clientParam, dispatch, setSessionClient]);

  const isAuthenticated = useAuth(onAuthenticated, redirectToB2CAuthWithImpersonation);

  useEffect(() => {
    // The auth check runs once per page load, so a client switch would otherwise go unreported.
    if (
      !isAuthenticated ||
      loadedClientParamRef.current === undefined ||
      loadedClientParamRef.current === clientParam
    ) {
      return;
    }

    // The placeholder address redirects to the resolved client (Pages home route) — not a switch.
    if (loadedClientParamRef.current === placeholderClientParam && clientParam === sessionClientCode?.toLowerCase()) {
      loadedClientParamRef.current = clientParam;
      return;
    }

    setSessionClient();
    dispatch(reportClientSwitch());
  }, [clientParam, isAuthenticated, sessionClientCode, setSessionClient, dispatch]);

  const clientPermissions = useMemo(() => {
    const result =
      clientParam === placeholderClientParam
        ? (permissions.find((p) => p.clientCode === sessionClientCode) ?? permissions[0])
        : permissions.find((p) => p.clientCode.toLowerCase() === clientParam);

    if (result) {
      updateClientCode(result.clientCode);
    }
    return result;
  }, [clientParam, permissions, sessionClientCode]);

  if (!isAuthenticated) {
    return <FullScreenLoader title="Please wait." subtitle="You are being redirected for authentication..." />;
  }

  if (identityLoadingState === IdentityLoadingState.AuthorizationOrAccessError) {
    return <AccessCheckPage />;
  }

  if (identityLoadingState === IdentityLoadingState.LoadingErrorImpersonationPermissions) {
    return <AuthenticationFailed />;
  }

  if (identityLoadingState === IdentityLoadingState.LoadingError) {
    return <Navigate to="/error" />;
  }

  if (identityLoadingState !== IdentityLoadingState.Loaded) {
    return <FullScreenLoader title="Please wait." subtitle="Loading user's data..." />;
  }

  if (clientPermissions === undefined) {
    return <ClientSelectionPage />;
  }

  const consentAgreementsToAccept = isImpersonationMode
    ? []
    : [...getUnsignedGeneralAgreements(userConsentAgreements), ...(clientPermissions.unsignedConsents ?? [])];

  const impersonationFundName = getImpersonationFundName(isImpersonationMode);

  return (
    <>
      <OutdatedBrowserAlert />
      <ErrorBoundary>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getUserLocaleInfo().locale}>
          <ClientContextProvider
            clientPermissions={clientPermissions}
            impersonationFundName={impersonationFundName}
            isImpersonationMode={isImpersonationMode}
          >
            <CustomThemeProvider>
              <NotificationContextProvider>
                <ImpersonationBlockContextProvider>
                  <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                    <Header />
                    {consentAgreementsToAccept.length > 0 ? (
                      <ConsentAgreementContainer consentAgreements={consentAgreementsToAccept} />
                    ) : (
                      <Pages />
                    )}
                    {isImpersonationMode && <ImpersonationPanel />}
                  </Box>
                  <GlobalNotificationSnackbar />
                  <GlobalNotificationErrorSnackbar />
                </ImpersonationBlockContextProvider>
              </NotificationContextProvider>
            </CustomThemeProvider>
          </ClientContextProvider>
        </LocalizationProvider>
      </ErrorBoundary>
    </>
  );
};

export default App;
